import { vm } from "../socket/config.js";
import { getUserInfo } from "../socket/events.js";
import { nameToTarget, targetToName } from "../utils/scratch-helpers.js";
import { getWorkspace, isWorkspaceAccesable } from "../utils/dom.js";
import { getBlock } from "../blocks/index.js";
import { delay } from "../utils/general.js";
import { resolveAvatarSrc } from "../../avatars/index.js";
import { getPeer } from "./players.js";

const STALE_MS = 12000;
const MARGIN = 34;
const WORKSPACE_SWAP_MS = 180;
const RETURN_COLOR = "#4a3f6b";

const editors = new Map(); // uid -> { username, color, avatar, target, blockId, x, y, updatedAt }
const els = new Map(); // uid -> HTMLElement
let returnPoint = null; // { target, x, y } in workspace coordinates
let returnEl = null;
let rafScheduled = false;

const STYLE_ID = "ls-remote-arrows-style";
const STYLE = `
    .ls-remote-arrow {
        position: fixed;
        z-index: 41;
        display: flex;
        align-items: center;
        gap: 4px;
        transform: translate(-50%, -50%);
        cursor: pointer;
        pointer-events: auto;
    }
    .ls-remote-arrow-chevron {
        flex-shrink: 0;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35));
        transition: transform 0.12s linear;
    }
    .ls-remote-arrow-pill {
        display: flex;
        align-items: center;
        gap: 5px;
        padding: 2px 10px 2px 2px;
        border-radius: 999px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
        white-space: nowrap;
    }
    .ls-remote-arrow-avatar {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 2px solid #fff;
        display: block;
    }
    .ls-remote-arrow-name {
        color: #fff;
        font-size: 0.7rem;
        font-weight: 600;
    }
    .ls-remote-arrow-icon {
        width: 20px;
        height: 20px;
        padding: 3px;
        box-sizing: border-box;
        border-radius: 50%;
        border: 2px solid #fff;
        background: rgba(255, 255, 255, 0.18);
        stroke: #fff;
        display: block;
    }
`;

const ensureStyles = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = STYLE;
    document.head.appendChild(style);
};

const createEl = (uid) => {
    ensureStyles();

    const el = document.createElement("div");
    el.className = "ls-remote-arrow";
    el.innerHTML = `
        <svg class="ls-remote-arrow-chevron" width="16" height="16" viewBox="0 0 24 24">
            <path d="M12 2 L19 18 L12 14 L5 18 Z" />
        </svg>
        <div class="ls-remote-arrow-pill">
            <img class="ls-remote-arrow-avatar" src="${resolveAvatarSrc(null)}" alt="" />
            <span class="ls-remote-arrow-name"></span>
        </div>
    `;
    el.addEventListener("click", () => navigateToEditor(uid));
    document.body.appendChild(el);
    els.set(uid, el);
    return el;
};

const getOrCreateEl = (uid) => els.get(uid) || createEl(uid);

const createReturnEl = () => {
    ensureStyles();

    const el = document.createElement("div");
    el.className = "ls-remote-arrow";
    el.innerHTML = `
        <svg class="ls-remote-arrow-chevron" width="16" height="16" viewBox="0 0 24 24">
            <path d="M12 2 L19 18 L12 14 L5 18 Z" fill="${RETURN_COLOR}" />
        </svg>
        <div class="ls-remote-arrow-pill" style="background: ${RETURN_COLOR}">
            <svg class="ls-remote-arrow-icon" viewBox="0 0 24 24" fill="none" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <path d="M19 12H5M11 18l-6-6 6-6"/>
            </svg>
            <span class="ls-remote-arrow-name">Volver</span>
        </div>
    `;
    el.title = "Volver a donde estabas";
    el.addEventListener("click", goBackToOrigin);
    document.body.appendChild(el);
    return el;
};

const getOrCreateReturnEl = () => {
    if (!returnEl) returnEl = createReturnEl();
    return returnEl;
};

const clearReturnPoint = () => {
    returnPoint = null;
    if (returnEl) {
        returnEl.remove();
        returnEl = null;
    }
};

const updateElContent = (el, entry) => {
    if (el.dataset.color !== entry.color) {
        el.dataset.color = entry.color;
        el.querySelector("path").setAttribute("fill", entry.color);
        el.querySelector(".ls-remote-arrow-pill").style.background =
            entry.color;
    }
    if (el.dataset.avatar !== String(entry.avatar)) {
        el.dataset.avatar = String(entry.avatar);
        el.querySelector(".ls-remote-arrow-avatar").src = resolveAvatarSrc(
            entry.avatar,
        );
    }
    if (el.dataset.username !== entry.username) {
        el.dataset.username = entry.username;
        const shown =
            entry.username?.length > 14
                ? `${entry.username.slice(0, 14)}...`
                : entry.username;
        el.querySelector(".ls-remote-arrow-name").textContent = shown || "";
    }
};

const positionEl = (el, x, y, angleDeg) => {
    el.style.display = "flex";
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.querySelector(".ls-remote-arrow-chevron").style.transform =
        `rotate(${angleDeg + 90}deg)`;
};

const hideEl = (uid) => {
    const el = els.get(uid);
    if (el) el.style.display = "none";
};

const getViewBounds = () => {
    if (!isWorkspaceAccesable()) return null;

    const workspace = getWorkspace();
    const parentSvg = workspace?.getParentSvg?.();
    if (!workspace || !parentSvg) return null;

    let metrics;
    try {
        metrics = workspace.getMetrics();
    } catch (e) {
        metrics = null;
    }
    if (!metrics) return null;

    const parentRect = parentSvg.getBoundingClientRect();
    const left = parentRect.left + (metrics.absoluteLeft || 0);
    const top = parentRect.top + (metrics.absoluteTop || 0);

    return {
        workspace,
        parentSvg,
        metrics,
        left,
        top,
        right: left + metrics.viewWidth,
        bottom: top + metrics.viewHeight,
    };
};

const getCtm = (bounds) => {
    const canvas = bounds.workspace.getCanvas?.();
    return canvas?.getScreenCTM?.() ?? null;
};

const toScreenPoint = (bounds, x, y) => {
    const ctm = getCtm(bounds);
    if (!ctm) return null;

    const pt = bounds.parentSvg.createSVGPoint();
    pt.x = x;
    pt.y = y;
    const transformed = pt.matrixTransform(ctm);

    return { x: transformed.x, y: transformed.y };
};

const toWorkspacePoint = (bounds, x, y) => {
    const ctm = getCtm(bounds);
    if (!ctm) return null;

    const pt = bounds.parentSvg.createSVGPoint();
    pt.x = x;
    pt.y = y;
    const transformed = pt.matrixTransform(ctm.inverse());

    return { x: transformed.x, y: transformed.y };
};

const isInsideView = (bounds, point) =>
    point.x >= bounds.left + MARGIN &&
    point.x <= bounds.right - MARGIN &&
    point.y >= bounds.top + MARGIN &&
    point.y <= bounds.bottom - MARGIN;

const edgePlacement = (bounds, point) => {
    const cx = (bounds.left + bounds.right) / 2;
    const cy = (bounds.top + bounds.bottom) / 2;

    let dx = point.x - cx;
    let dy = point.y - cy;
    if (dx === 0 && dy === 0) dy = -1;

    const halfW = Math.max(bounds.right - bounds.left, MARGIN * 2) / 2 - MARGIN;
    const halfH = Math.max(bounds.bottom - bounds.top, MARGIN * 2) / 2 - MARGIN;
    const scale = Math.min(
        Math.abs(halfW / (dx || 1e-6)),
        Math.abs(halfH / (dy || 1e-6)),
    );

    return {
        x: cx + dx * scale,
        y: cy + dy * scale,
        angleDeg: (Math.atan2(dy, dx) * 180) / Math.PI,
    };
};

const centerOnWorkspacePoint = (workspace, x, y) => {
    if (!workspace?.scrollbar) return;

    const metrics = workspace.getMetrics();
    const scale = workspace.scale || 1;

    workspace.scrollbar.set(
        x * scale - metrics.contentLeft - metrics.viewWidth / 2,
        y * scale - metrics.contentTop - metrics.viewHeight / 2,
    );
};

const reposition = (uid) => {
    const entry = editors.get(uid);
    if (!entry) return;

    const bounds = getViewBounds();
    if (!bounds) {
        hideEl(uid);
        return;
    }

    const el = getOrCreateEl(uid);
    updateElContent(el, entry);

    if (targetToName(vm.editingTarget) !== entry.target) {
        el.style.display = "none";
        return;
    }

    const point = toScreenPoint(bounds, entry.x, entry.y);
    if (!point || isInsideView(bounds, point)) {
        el.style.display = "none";
        return;
    }

    const placement = edgePlacement(bounds, point);
    positionEl(el, placement.x, placement.y, placement.angleDeg);
};

const repositionReturn = () => {
    if (!returnPoint) return;

    const bounds = getViewBounds();
    if (!bounds) {
        if (returnEl) returnEl.style.display = "none";
        return;
    }

    if (!nameToTarget(returnPoint.target)) {
        clearReturnPoint();
        return;
    }

    const el = getOrCreateReturnEl();

    // On another sprite the spot cannot be projected, so the arrow waits on the
    // left edge until the user comes back to the sprite they started from
    if (targetToName(vm.editingTarget) !== returnPoint.target) {
        positionEl(
            el,
            bounds.left + MARGIN,
            (bounds.top + bounds.bottom) / 2,
            180,
        );
        return;
    }

    const point = toScreenPoint(bounds, returnPoint.x, returnPoint.y);
    if (!point) {
        el.style.display = "none";
        return;
    }

    if (isInsideView(bounds, point)) {
        clearReturnPoint();
        return;
    }

    const placement = edgePlacement(bounds, point);
    positionEl(el, placement.x, placement.y, placement.angleDeg);
};

const destroyEditor = (uid) => {
    editors.delete(uid);
    const el = els.get(uid);
    if (el) {
        el.remove();
        els.delete(uid);
    }
};

const tick = () => {
    const now = Date.now();
    for (const [uid, entry] of editors) {
        if (now - entry.updatedAt > STALE_MS) destroyEditor(uid);
    }

    for (const uid of editors.keys()) reposition(uid);
    repositionReturn();

    if (editors.size > 0 || returnPoint) {
        requestAnimationFrame(tick);
    } else {
        rafScheduled = false;
    }
};

const ensureLoopRunning = () => {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(tick);
};

const waitForBlockInWorkspace = (blockId, timeoutMs) =>
    new Promise((resolve) => {
        const start = Date.now();
        const check = () => {
            if (getBlock(blockId) || Date.now() - start > timeoutMs) {
                resolve();
                return;
            }
            setTimeout(check, 60);
        };
        check();
    });

const captureOrigin = () => {
    const bounds = getViewBounds();
    if (!bounds) return null;

    const center = toWorkspacePoint(
        bounds,
        (bounds.left + bounds.right) / 2,
        (bounds.top + bounds.bottom) / 2,
    );
    if (!center) return null;

    return {
        target: targetToName(vm.editingTarget),
        x: center.x,
        y: center.y,
    };
};

export const goBackToOrigin = async () => {
    const origin = returnPoint;
    if (!origin) return;

    const target = nameToTarget(origin.target);
    if (!target) {
        clearReturnPoint();
        return;
    }

    if (vm.editingTarget !== target) {
        vm.setEditingTarget(target.id);
        // Blockly rebuilds the workspace when the sprite changes, and scrolling
        // before that is done gets overwritten
        await delay(WORKSPACE_SWAP_MS);
    }

    centerOnWorkspacePoint(getWorkspace(), origin.x, origin.y);
    clearReturnPoint();
};

export const navigateToEditor = async (uid) => {
    const entry = editors.get(uid);
    if (!entry) return;

    const target = nameToTarget(entry.target);
    if (!target) return;

    const origin = captureOrigin();

    if (vm.editingTarget !== target) {
        vm.setEditingTarget(target.id);
        await waitForBlockInWorkspace(entry.blockId, 2000);
    }

    const workspace = getWorkspace();
    if (!workspace) return;

    if (
        getBlock(entry.blockId) &&
        typeof workspace.centerOnBlock === "function"
    ) {
        workspace.centerOnBlock(entry.blockId);
    } else {
        centerOnWorkspacePoint(workspace, entry.x, entry.y);
    }

    // Set only once the view has moved, otherwise the spot is still on screen
    // and the arrow would clear itself on the next frame
    if (origin?.target) {
        returnPoint = origin;
        ensureLoopRunning();
    }
};

export const onEditPosition = (data) => {
    if (!data?.uid || !data?.data) return;
    if (data.uid === getUserInfo().uid) return;

    const { target, blockId, x, y } = data.data;
    if (!target || !blockId) return;

    editors.set(data.uid, {
        username: data.username,
        color: data.color || "#7c5cff",
        avatar: getPeer(data.uid)?.avatar ?? null,
        target,
        blockId,
        x,
        y,
        updatedAt: Date.now(),
    });

    ensureLoopRunning();
};

export const onRemoteArrowPlayersUpdate = (players) => {
    const activeUids = new Set((players || []).map((p) => p.uid));
    for (const uid of Array.from(editors.keys())) {
        if (!activeUids.has(uid)) destroyEditor(uid);
    }
};
